// TradingRoomPage — Live trading room w/ chat + real-time signals
// Layout: top stats bar, live signal feed (sticky), chat stream, composer

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';

interface Message {
  id: string;
  user: string;
  avatar: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  text: string;
  time: string;
  isMod?: boolean;
  isSignal?: boolean;
  trade?: { pair: string; direction: 'long' | 'short'; entry: number; target: number; stop: number; pnl?: number };
}

interface Signal {
  id: string;
  pair: string;
  direction: 'long' | 'short';
  entry: number;
  target: number;
  stop: number;
  current: number;
  trader: string;
  avatar: string;
  time: string;
  pnl: number;
  pnlPct: number;
  status: 'open' | 'closed';
}

const SIGNALS: Signal[] = [
  { id: 's1', pair: 'NQ', direction: 'long',  entry: 18245, target: 18300, stop: 18220, current: 18278, trader: 'Marcus Chen', avatar: 'user-1', time: '14m', pnl: 165,  pnlPct: 0.18, status: 'open' },
  { id: 's2', pair: 'ES', direction: 'short', entry: 5305,  target: 5270,  stop: 5320,  current: 5285,  trader: 'Sofia Petrov', avatar: 'user-2', time: '2h',  pnl: 480,  pnlPct: 0.38, status: 'open' },
  { id: 's3', pair: 'GC', direction: 'long',  entry: 2352,  target: 2370,  stop: 2345,  current: 2365,  trader: 'Aiden Wright', avatar: 'user-3', time: '3h',  pnl: 130,  pnlPct: 0.55, status: 'open' },
  { id: 's4', pair: 'CL', direction: 'short', entry: 79.5,  target: 77.8,  stop: 80.2,  current: 79.5,  trader: 'Diego Rivera', avatar: 'user-3', time: '5h',  pnl: 0,    pnlPct: 0,    status: 'open' },
];

const INITIAL_MESSAGES: Message[] = [
  { id: 'm1', user: 'Marcus Chen',    avatar: 'user-1', tier: 'Professional', text: 'NQ just broke 18,240 resistance on heavy volume. Long entry triggered.', time: '14m', isSignal: true, trade: { pair: 'NQ', direction: 'long', entry: 18245, target: 18300, stop: 18220, pnl: 165 } },
  { id: 'm2', user: 'Aiden Wright',   avatar: 'user-3', tier: 'Professional', text: 'Confirming on my charts too. Volume profile aligns.', time: '13m' },
  { id: 'm3', user: 'Sofia Petrov',   avatar: 'user-2', tier: 'Professional', text: 'Sold ES at 5305. S&P looking weak here. Target 5270.', time: '2h', isSignal: true, trade: { pair: 'ES', direction: 'short', entry: 5305, target: 5270, stop: 5320, pnl: 480 } },
  { id: 'm4', user: 'Trading Room Mod', avatar: 'mod', tier: 'Beginner', text: 'Reminder: Set stops BEFORE entering. No exceptions.', time: '2h', isMod: true },
  { id: 'm5', user: 'Lena Voss',      avatar: 'user-4', tier: 'Mid',          text: 'GC setting up nicely on the daily. Long bias.', time: '3h' },
  { id: 'm6', user: 'Yuki Tanaka',    avatar: 'user-4', tier: 'Mid',          text: 'Anyone watching CL? OPEC meeting tomorrow could move it.', time: '4h' },
  { id: 'm7', user: 'Diego Rivera',   avatar: 'user-3', tier: 'Professional', text: 'Watching 80.2 resistance. Short scalp setup if it fails.', time: '4h', isSignal: true, trade: { pair: 'CL', direction: 'short', entry: 79.5, target: 77.8, stop: 80.2 } },
  { id: 'm8', user: 'Aisha Khan',     avatar: 'user-2', tier: 'Mid',          text: 'Just hit my NQ target +$420! Thanks Marcus 🎯', time: '5h' },
];

const ONLINE_USERS = 287;

export function TradingRoomPage() {
  const nav = useNavigate();
  const [msgs, setMsgs] = useState(INITIAL_MESSAGES);
  const [draft, setDraft] = useState('');
  const [showSignalPanel, setShowSignalPanel] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  function send() {
    if (!draft.trim()) return;
    setMsgs((curr) => [...curr, {
      id: String(Date.now()),
      user: 'You', avatar: 'me', tier: 'Professional',
      text: draft, time: 'now',
    }]);
    setDraft('');
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs]);

  return (
    <div className="flex flex-col bg-tf-surface-1" style={{ height: '100vh' }}>
      {/* Top nav */}
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
          <div className="text-[15px] font-semibold">Live · {ONLINE_USERS} traders</div>
        </div>
        <button onClick={() => setShowParticipants(true)} data-testid="participants" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Live signal strip */}
      <div className="shrink-0 border-b border-tf-border bg-tf-text-1">
        <div className="flex items-center gap-1 px-2 py-1.5 overflow-x-auto">
          {SIGNALS.map((s) => (
            <div key={s.id} data-testid={`signal-${s.id}`} className="shrink-0 rounded-[10px] bg-[#2C2C2E] px-2.5 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className={['text-[10px] font-bold', s.direction === 'long' ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                  {s.pair} {s.direction === 'long' ? '▲' : '▼'}
                </span>
                <span className="text-[11px] text-white tabular-nums">{s.current.toFixed(2)}</span>
                <span className={['text-[10px] font-bold tabular-nums', s.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                  {s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(0)}
                </span>
              </div>
            </div>
          ))}
          <button onClick={() => setShowSignalPanel(true)} data-testid="add-signal" className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#34C759] text-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
          </button>
        </div>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
        {msgs.map((m) => (
          <div key={m.id} data-testid={`msg-${m.id}`} className="flex items-start gap-2">
            <UserAvatar size={32} name={m.user} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-bold text-tf-text-1">{m.user}</span>
                {m.isMod && <span className="rounded bg-[#FF9500] px-1 py-0.5 text-[8px] font-bold uppercase text-white">MOD</span>}
                <TierPill tier={m.tier} />
                <span className="text-[10px] text-tf-text-3">{m.time}</span>
              </div>
              <p className="mt-0.5 text-[13px] text-tf-text-1 leading-snug">{m.text}</p>
              {m.trade && (
                <div className="mt-1 flex items-center gap-2 rounded-[10px] border border-tf-border bg-white p-2">
                  <div className={['flex h-8 w-8 items-center justify-center rounded-[8px] text-[10px] font-bold',
                    m.trade.direction === 'long' ? 'bg-[#34C759]/15 text-tf-profit' : 'bg-[#FF3B30]/15 text-tf-loss'].join(' ')}>
                    {m.trade.pair}
                  </div>
                  <div className="flex-1 text-[11px]">
                    <div className="font-semibold text-tf-text-1">{m.trade.pair} · {m.trade.direction.toUpperCase()}</div>
                    <div className="text-tf-text-3 tabular-nums">Entry {m.trade.entry} · TP {m.trade.target} · SL {m.trade.stop}</div>
                  </div>
                  <button onClick={() => alert('Copied to your bot')} data-testid="copy-trade" className="h-7 rounded-full bg-tf-blue px-2.5 text-[10px] font-semibold text-white">Copy</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-tf-border bg-white p-2 flex items-end gap-1.5" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        <button onClick={() => setShowSignalPanel(true)} data-testid="signal-attach" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tf-surface-1 text-tf-text-1 active:opacity-40">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Send a message or share a signal..."
          data-testid="chat-input"
          rows={1}
          className="flex-1 max-h-20 resize-none rounded-[18px] bg-tf-surface-1 px-3 py-2 text-[14px] text-tf-text-1 focus:outline-none placeholder:text-tf-text-3"
        />
        <button onClick={send} data-testid="chat-send" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tf-blue text-white active:opacity-40">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z" /></svg>
        </button>
      </div>

      {/* Signal composer */}
      {showSignalPanel && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowSignalPanel(false)}>
          <div className="ios26-sheet w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Share a signal</h2>
            <p className="px-4 pb-3 text-[13px] text-tf-text-3">Alert the room to your trade setup</p>
            <div className="space-y-2.5 px-1">
              <div className="flex gap-1.5">
                {['ES', 'NQ', 'CL', 'GC', 'BTC', 'YM'].map((p) => (
                  <button key={p} className="flex-1 h-10 rounded-full bg-white border border-tf-border text-[13px] font-semibold active:opacity-40" data-testid={`sigpair-${p}`}>{p}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button className="h-12 rounded-[12px] bg-[#34C759] text-[14px] font-bold text-white active:opacity-40" data-testid="sig-long">LONG</button>
                <button className="h-12 rounded-[12px] bg-[#FF3B30] text-[14px] font-bold text-white active:opacity-40" data-testid="sig-short">SHORT</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-[12px] border border-tf-border bg-white p-2"><div className="text-[9px] uppercase text-tf-text-3">Entry</div><input className="w-full bg-transparent text-[14px] font-bold tabular-nums" defaultValue="18250" /></div>
                <div className="rounded-[12px] border border-tf-border bg-white p-2"><div className="text-[9px] uppercase text-tf-text-3">Target</div><input className="w-full bg-transparent text-[14px] font-bold tabular-nums" defaultValue="18300" /></div>
                <div className="rounded-[12px] border border-tf-border bg-white p-2"><div className="text-[9px] uppercase text-tf-text-3">Stop</div><input className="w-full bg-transparent text-[14px] font-bold tabular-nums" defaultValue="18220" /></div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setShowSignalPanel(false)} className="flex-1 h-12 rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
              <button onClick={() => { alert('Signal shared with the room'); setShowSignalPanel(false); }} data-testid="submit-signal" className="flex-1 h-12 btn-spring">Post signal</button>
            </div>
          </div>
        </div>
      )}

      {/* Participants */}
      {showParticipants && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowParticipants(false)}>
          <div className="ios26-sheet w-full max-w-[420px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">In the room</h2>
            <p className="px-4 pb-3 text-[13px] text-tf-text-3">{ONLINE_USERS} traders online</p>
            {[
              { n: 'Marcus Chen',  a: 'user-1', t: 'Professional', role: 'Top trader' },
              { n: 'Sofia Petrov', a: 'user-2', t: 'Professional', role: 'Mod' },
              { n: 'Aiden Wright', a: 'user-3', t: 'Professional', role: 'Bot creator' },
              { n: 'Lena Voss',    a: 'user-4', t: 'Mid' },
              { n: 'Yuki Tanaka',  a: 'user-4', t: 'Mid' },
              { n: 'Diego Rivera', a: 'user-3', t: 'Professional' },
              { n: 'Aisha Khan',   a: 'user-2', t: 'Mid' },
              { n: 'Ravi Patel',   a: 'user-1', t: 'Beginner' },
            ].map((p) => (
              <div key={p.n} className="flex items-center gap-3 px-1 py-2 active:bg-tf-surface-1 rounded-[12px]" data-testid={`participant-${p.n}`}>
                <div className="relative">
                  <UserAvatar size={36} name={p.n} />
                  <div className="absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full bg-[#34C759] border-2 border-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[14px] font-semibold text-tf-text-1">{p.n}</span>
                    <TierPill tier={p.t as any} />
                  </div>
                  {p.role && <div className="text-[10px] text-tf-text-3">{p.role}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
